'use client';

import { Button, cn, Skeleton } from '@shared/components/shadcn';
import { NumberFormats, useCurrencyFormatter } from '@shared/utils';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { useGlobalSheet } from '../../../../global-sheets/context/GlobalSheetContext';
import { useIsAccountSuspended } from '../../../hooks/useIsAccountSuspended';

type TotalBalanceProps = {
  namespace: string;
  availableBalance?: number;
  className?: string;
  onDepositClick?: () => void;
};

// Will be 48px at 7 characters, e.g. £150.00, and will get smaller as the number of characters increases
const calculateFontSize = (charactersLength: number) => 48 + (17.5 - 2.5 * charactersLength);

function TotalBalance({ namespace, availableBalance, className, onDepositClick }: TotalBalanceProps) {
  const { openDepositSheet } = useGlobalSheet();
  const t = useTranslations(namespace);
  const formatCurrency = useCurrencyFormatter();
  const isAccountSuspended = useIsAccountSuspended();

  const { formattedBalance, totalBalanceFontSize } = useMemo(() => {
    const formatted = formatCurrency(availableBalance || 0)
      .format(NumberFormats.WITH_DECIMALS)
      .get();

    return {
      formattedBalance: formatted,
      totalBalanceFontSize: calculateFontSize(formatted.length),
    };
  }, [availableBalance, formatCurrency]);

  return (
    <div className={cn('flex justify-between items-center', className)} data-track-container="Balance">
      <div className="flex flex-col">
        <p className="paragraph-explanatory" data-testid="total-balance-label">
          {t('total_balance')}
        </p>
        {availableBalance || availableBalance === 0 ? (
          <h2 style={{ fontSize: `${totalBalanceFontSize}px` }} data-testid="total-balance-amount">
            {formattedBalance}
          </h2>
        ) : (
          <Skeleton className="w-full h-10" />
        )}
      </div>

      {!isAccountSuspended && (
        <Button
          className="btn-label py-3"
          variant="accent"
          size="s"
          data-track-name="Deposit"
          data-testid="deposit-button"
          onClick={() => {
            openDepositSheet();
            onDepositClick?.();
          }}
        >
          {t('deposit_button')}
        </Button>
      )}
    </div>
  );
}

export { TotalBalance };
